import { rtkApi } from '../rtkApi';

export interface BridgeRatesByTerm {
  sixMonth: number;
  twelveMonth: number;
  eighteenMonth: number;
  twentyFourMonth: number;
}

export interface UserInterestRate {
  conventional: number;
  fha: number;
  va: number;
  usda: number;
  dscr: number;
  commercial: number;
  fixAndFlip: number;
  bridge?: number;
  bridgeRatesByTerm?: BridgeRatesByTerm;
  lastUpdated: string;
}

export interface ProgramInterestRate {
  rate: number;
  lastUpdated: string;
}

export interface CreditScoreAdjustments {
  excellent: number;
  good: number;
  fair: number;
  poor: number;
}

export interface LTVAdjustments {
  low: number;
  moderate: number;
  high: number;
  veryHigh: number;
}

export interface AdjustedRateRequest {
  loanProgram: string;
  creditScore: number;
  ltv: number;
}

export interface AdjustedRateResponse {
  adjustedRate: number;
}

export interface InterestRateCommandCenter {
  effectiveDate: string;
  lastUpdated: string;
  loanProgram: string;
  loanProgramRequested: string;
  baseRates: {
    conventional: number;
    fha: number;
    va: number;
    usda: number;
    dscr: number;
    fixAndFlip: number;
  };
  adjustedRatePercent: number;
  priorAdjustedRatePercent: number | null;
  rateChangeFromPrior: number | null;
  buyingPower: number;
  priorBuyingPower: number | null;
  buyingPowerDelta: number | null;
  buyingPowerDeltaPercent: number | null;
  lockFloatSuggestion: 'lock' | 'float';
  lockFloatRationale: string;
  inputsUsed: {
    creditScore: number;
    ltv: number;
    monthlyIncome: number;
    monthlyDebt: number;
    downPayment: number;
    loanTerm: number;
  };
}

export type CommandCenterQueryParams = {
  loanProgram?: string;
  creditScore?: number;
  ltv?: number;
  monthlyIncome?: number;
  monthlyDebt?: number;
  downPayment?: number;
  loanTerm?: number;
};

export const userInterestRateApi = rtkApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    // Get current interest rates
    getCurrentRates: builder.query<UserInterestRate, void>({
      query: () => ({
        url: '/interest-rates/current',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: UserInterestRate;
      }) => {
        // Log when rates are fetched (RTK Query caches, so this won't spam)
        const lastLogged = (window as any).__lastRateApiLog;
        const shouldLog =
          !lastLogged || lastLogged !== response.data.lastUpdated;

        if (shouldLog) {
          console.log('[ProductDetail Rate API] Backend rates received', {
            rates: response.data,
            lastUpdated: response.data.lastUpdated,
            timestamp: new Date().toISOString(),
            source: 'backend-api',
          });
          (window as any).__lastRateApiLog = response.data.lastUpdated;
        }
        return response.data;
      },
      providesTags: ['UserInterestRates'],
    }),

    // Get rate for specific loan program
    getRateForProgram: builder.query<ProgramInterestRate, string>({
      query: (loanProgram) => ({
        url: `/interest-rates/program/${loanProgram}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: ProgramInterestRate;
      }) => {
        return response.data;
      },
      providesTags: ['UserInterestRates'],
    }),

    // Get credit score adjustments
    getCreditScoreAdjustments: builder.query<CreditScoreAdjustments, void>({
      query: () => ({
        url: '/interest-rates/credit-score-adjustments',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: CreditScoreAdjustments;
      }) => {
        return response.data;
      },
      providesTags: ['UserInterestRates'],
    }),

    // Get LTV adjustments
    getLTVAdjustments: builder.query<LTVAdjustments, void>({
      query: () => ({
        url: '/interest-rates/ltv-adjustments',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: LTVAdjustments;
      }) => {
        return response.data;
      },
      providesTags: ['UserInterestRates'],
    }),

    // Calculate adjusted rate for user
    calculateAdjustedRate: builder.mutation<
      AdjustedRateResponse,
      AdjustedRateRequest
    >({
      query: (data) => ({
        url: '/interest-rates/calculate-adjusted',
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: {
        success: boolean;
        data: AdjustedRateResponse;
      }) => {
        return response.data;
      },
      invalidatesTags: ['UserInterestRates'],
    }),

    // Personalized snapshot (admin rate history + buying power + lock/float hint)
    getCommandCenter: builder.query<
      InterestRateCommandCenter,
      CommandCenterQueryParams | void
    >({
      query: (arg) => {
        const params =
          arg && typeof arg === 'object'
            ? Object.fromEntries(
                Object.entries(arg).filter(
                  ([, v]) =>
                    v !== undefined &&
                    v !== null &&
                    v !== '' &&
                    !Number.isNaN(v)
                )
              )
            : undefined;
        return {
          url: '/interest-rates/command-center',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: {
        success: boolean;
        data: InterestRateCommandCenter;
      }) => response.data,
      providesTags: ['UserInterestRates'],
    }),

    // Get rate matrices (for all users, not just admins)
    getRateMatrices: builder.query<any, void>({
      query: () => ({
        url: '/interest-rates/matrices',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: any }) => {
        return response.data;
      },
      providesTags: ['UserInterestRates'],
      // Poll every 30 seconds to get updates when admin changes rates
      pollingInterval: 30000,
    }),
  }),
});

export const {
  useGetCurrentRatesQuery,
  useGetRateForProgramQuery,
  useGetCreditScoreAdjustmentsQuery,
  useGetLTVAdjustmentsQuery,
  useCalculateAdjustedRateMutation,
  useGetRateMatricesQuery,
  useGetCommandCenterQuery,
} = userInterestRateApi;
